import React, { useState, useEffect, useRef } from 'react';
import { OutlinedInput, OutlinedInputProps } from '@mui/material';

interface BrazilianNumberInputProps extends Omit<OutlinedInputProps, 'onChange' | 'value'> {
    value?: number;
    onChange?: (value: number) => void;
    decimals?: number;
    prefix?: string;
    allowNegative?: boolean;
}

export default function BrazilianNumberInput({
    value,
    onChange,
    decimals = 4,
    prefix = '',
    allowNegative = false,
    ...props
}: BrazilianNumberInputProps) {
    const [displayValue, setDisplayValue] = useState<string>('');
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const inputRef = useRef<HTMLInputElement>(null);

    // Formata o número para exibição no padrão brasileiro
    const formatNumber = (num: number | undefined): string => {
        if (num === undefined || num === null || isNaN(num)) return '';

        const numStr = num.toFixed(decimals);
        const [intPart, decPart] = numStr.split('.');

        // Formata parte inteira com separador de milhares
        const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

        // Retorna com vírgula como separador decimal
        return decPart ? `${formattedInt},${decPart}` : formattedInt;
    };

    // Atualiza o display quando o valor externo mudar (mas não quando estiver focado)
    useEffect(() => {
        if (!isFocused) {
            if (value !== undefined && value !== null && !isNaN(value)) {
                setDisplayValue(formatNumber(value));
            } else {
                setDisplayValue('');
            }
        }
    }, [value, isFocused]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let inputValue = e.target.value;

        // Remove o prefixo se existir
        if (prefix && inputValue.startsWith(prefix)) {
            inputValue = inputValue.substring(prefix.length).trim();
        }

        // Remove tudo exceto números, vírgula e ponto
        inputValue = inputValue.replace(/[^\d,.]/g, '');

        // Permite apenas uma vírgula ou ponto
        const commaCount = (inputValue.match(/,/g) || []).length;
        const dotCount = (inputValue.match(/\./g) || []).length;

        if (commaCount > 1 || dotCount > 1 || (commaCount > 0 && dotCount > 0)) {
            return;
        }

        setDisplayValue(inputValue);

        // Converte para número
        const normalizedValue = inputValue
            .replace(/\./g, '') // Remove pontos (separadores de milhares)
            .replace(',', '.'); // Substitui vírgula por ponto

        const numericValue = parseFloat(normalizedValue);

        if (!isNaN(numericValue)) {
            if (!allowNegative && numericValue < 0) {
                return;
            }
            onChange?.(numericValue);
        } else if (inputValue === '' || inputValue === ',' || inputValue === '.') {
            onChange?.(0);
        }
    };

    const handleBlur = () => {
        setIsFocused(false);
        // Formata o valor ao perder o foco
        if (value !== undefined && value !== null && !isNaN(value)) {
            setDisplayValue(formatNumber(value));
        } else {
            setDisplayValue('');
        }
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(true);
        // Remove a formatação ao focar para facilitar a edição
        if (value !== undefined && value !== null && !isNaN(value)) {
            const rawValue = value.toString().replace('.', ',');
            setDisplayValue(rawValue);
        }
        // Seleciona todo o texto ao focar
        setTimeout(() => {
            e.target.select();
        }, 0);
    };

    return (
        <OutlinedInput
            {...props}
            inputRef={inputRef}
            value={displayValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            inputProps={{
                ...props.inputProps,
                inputMode: 'decimal',
            }}
            startAdornment={prefix ? <span style={{ marginRight: '4px' }}>{prefix}</span> : undefined}
        />
    );
}
